import { Error } from "@progress/kendo-react-labels";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import productsApi from "../../../api/products";
import quotesApi from "../../../api/quotes";
import { useUserContext } from "../../../hooks/useUserContext";
import { LoadingIndicator } from "../../LoadingIndicator";
import { Commodity } from "../Commodity";
import { Instrument } from "../Instrument";
import { AccumulatorQuoteDetails } from "../accumulator";
import { InsuranceQuoteDetails } from "../insurance";
import { VanillaQuoteDetails } from "../vanilla/VanillaQuoteDetails";

type Props = {
  quoteId: number;
  onReject: () => void;
};

type Quote = {
  id: number;
  commodityId: number;
  instrumentId: number;
  productType: string;
};

const INSURANCE = "Insurance";
const VANILLA = "Vanilla";
const ACCUMULATOR = "Accumulator";

export const QuoteDetailsSummary = (props: Props) => {
  const { quoteId, onReject } = props;

  const queryClient = useQueryClient();

  const { context: user } = useUserContext();
  const isRiskTaker = user.roles.isRiskTaker;

  const results = useQueries({
    queries: [
      {
        queryKey: ["commodities"],
        queryFn: () => productsApi.getCommodities(),
      },
      {
        queryKey: ["instruments"],
        queryFn: () => productsApi.getInstruments(),
      },
      {
        queryKey: ["quote", quoteId],
        queryFn: () => quotesApi.getDetails(quoteId),
      },
    ],
    combine: (results: any) => {
      return {
        data: results.map((result: any) => result.data),
        pending: results.some((result: any) => result.isPending),
        error: results
          .filter((result: any) => result.error)
          .map((err: any) => err.error)
          .join(" "),
      };
    },
  });

  if (results.pending) {
    return <LoadingIndicator loading={true} />;
  }

  if (results.error) {
    return <Error>{results.error}</Error>;
  }

  const [commoditiesData = {}, instrumentsData = {}, quoteData = {}] = results?.data ?? [];

  const commodities = commoditiesData.commodities ?? ([] as Commodity[]);
  const instruments = instrumentsData.instruments ?? ([] as Instrument[]);
  const quote = quoteData.quote ?? ({} as Quote);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["quote", quoteId] });
  };

  const commodity = Commodity.create(
    commodities?.find((commodity: any) => commodity.id === quote?.commodityId),
  );
  const instrument = Instrument.create(
    instruments?.find(
      (instrument: any) =>
        instrument.id === quote?.instrumentId && instrument.productType === quote?.productType,
    ),
  );

  const quoteType =
    quote?.productType === INSURANCE
      ? INSURANCE
      : instrument.name === ACCUMULATOR
        ? ACCUMULATOR
        : instrument.name
          ? VANILLA
          : "";

  return (
    <>
      {quoteType === INSURANCE ? (
        <InsuranceQuoteDetails
          quote={quote}
          refetch={refetch}
          commodity={commodity}
          instrument={instrument}
          isRiskTaker={isRiskTaker}
          onReject={onReject}
        />
      ) : quoteType === ACCUMULATOR ? (
        <AccumulatorQuoteDetails
          quote={quote}
          refetch={refetch}
          commodity={commodity}
          instrument={instrument}
          isRiskTaker={isRiskTaker}
          onReject={onReject}
        />
      ) : quoteType === VANILLA ? (
        <VanillaQuoteDetails
          quote={quote}
          refetch={refetch}
          commodity={commodity}
          instrument={instrument}
          isRiskTaker={isRiskTaker}
          onReject={onReject}
        />
      ) : (
        <></>
      )}
    </>
  );
};
